import { Injectable, inject } from "@angular/core";
import _ from "lodash";
import { Observable } from "rxjs";
import { Mattslag } from "../../../common/models/constants";
import { VKProduktGrupp, Virkeskostnad } from "../models/virkeskostnader/virkeskostnader-models";
import { ForkalkylService } from "./forkalkyl.service";

@Injectable()
export class VirkeskostnaderUtbyteService {
    private readonly forkalkylService = inject(ForkalkylService);

    private readonly ejHanteratUtbyteWarnings: any[]=[];
    private ejHanteratUtbyte: VKProduktGrupp[]=[];
    private utbyteTotalVolymM3fub?: number;
    private atgardNettoareal?: number;
    private readonly mattslagsKonverteringar: any [] = [];

    static readonly utbyteMetodSnabbkalkyl = "Snabbkalkyl";
    static readonly utbyteMetodDirektutbyte = "Direktutbyte";

    get utbyteWarnings(): any[] {
        return this.ejHanteratUtbyteWarnings;
    }

    static arealDiffMessage(utbyteInlastFran: string): string {
        if(utbyteInlastFran === this.utbyteMetodSnabbkalkyl) {
            return `Arealen har förändrats och ny "Hämta beräknat utbyte" bör göras från ${  this.utbyteMetodSnabbkalkyl}.`;
        }else if(utbyteInlastFran === this.utbyteMetodDirektutbyte) {
            return `Arealen har förändrats ny beräkning och ny "Hämta beräknat utbyte" bör göras från ${  this.utbyteMetodDirektutbyte}.`;
        } else {
            return "Okänd typ på utbyteInlastFran";
        }
    }

    static arealDiffers(nettoareal: number, utbyteInlastAreal?: number): boolean {
        if(nettoareal && utbyteInlastAreal) {
            const difference = _.round(Math.abs(utbyteInlastAreal - nettoareal), 1);
            return difference > 0.1;
        }
        return false;
    }

    // används för att kontrollera ej hanterat utbyte från andra komponenter (efter imoprt).
    checkEjHanteratUtbyte(virkeskostnader: Virkeskostnad[]): void {
        if(!_.isNil(this.ejHanteratUtbyte) && this.ejHanteratUtbyte.length > 0) {
            this.handleEjHanteratUtbyte(this.atgardNettoareal, virkeskostnader,
                this.ejHanteratUtbyte, this.utbyteTotalVolymM3fub);
        }
    }

    // Används i beraknat-utbyte-button och sätter värdena vid importen.
    handleEjHanteratUtbyte(atgardNettoareal: number, virkeskostnader: Virkeskostnad[],
        ejHanteratUtbyte: VKProduktGrupp[], utbyteTotalVolymM3fub: number) {

        if(utbyteTotalVolymM3fub === 0 || ejHanteratUtbyte.length === 0) {
            return;
        }

        this.ejHanteratUtbyte = ejHanteratUtbyte;
        this.utbyteTotalVolymM3fub = utbyteTotalVolymM3fub;
        this.atgardNettoareal = atgardNettoareal;

        let totalVolumM3fub = 0;
        virkeskostnader.forEach(x => {
            if(x.mattslag === Mattslag.M3F) {
                totalVolumM3fub = totalVolumM3fub + (x.kvantitet ?? 0);
            } else {
                const isHSGrupp = x.handelssortimentsgruppId !== null && x.handelssortimentId === null;
                let konverteringsfaktor: number;

                if(isHSGrupp) {
                    konverteringsfaktor = this.getKonverteringsfaktorForHSgrupp(x.handelssortimentsgruppId, x.mattslag);
                } else {
                    konverteringsfaktor = this.getKonverteringsfaktorForHS(x.handelssortimentId, x.mattslag);
                }

                const produkt = (x.kvantitet ?? 0) * konverteringsfaktor;
                totalVolumM3fub = totalVolumM3fub + produkt;
            }
        });

        // tillåter en felmarginal på 1 för att ta hänsyn till eventuella avrundningsfel
        if(this.utbyteTotalVolymM3fub - totalVolumM3fub <= 1) {
            this.ejHanteratUtbyte?.splice(0);
        }

        this.handleEjHanteratUtbyteWarnings();
    }

    getKonverteringsfaktorForHSgrupp(handelssortimentsgruppId: number, mattslag: string): number {
        return this.mattslagsKonverteringar
            .find(x => x.handelssortimentId === null &&
                x.handelssortimentsgruppId === handelssortimentsgruppId &&
                x.mattslag === mattslag).konverteringsfaktor;
    }

    getKonverteringsfaktorForHS(handelssortimentId: number, mattslag: string): number {
        return this.mattslagsKonverteringar
            .find(x => x.handelssortimentId === handelssortimentId &&
                x.handelssortimentId !== null &&
                x.mattslag === mattslag).konverteringsfaktor;
    }

    getMattslagsKonverteringar(virkeskostnader: Virkeskostnad[]): void {
        virkeskostnader?.forEach(vk => {
            if (vk.mattslag !== Mattslag.M3F && !this.mattslagskonverteringExists(vk)) {
                let konverteringsfaktor = 0;
                if (vk.konverteringsfaktorHandelssortimentMattslag) {
                    konverteringsfaktor = vk.konverteringsfaktorHandelssortimentMattslag;
                    this.mattslagsKonverteringar.push(
                        {
                            mattslag: vk.mattslag,
                            handelssortimentId: vk.handelssortimentId,
                            handelssortimentsgruppId: vk.handelssortimentsgruppId,
                            konverteringsfaktor: konverteringsfaktor
                        });
                } else {
                    const isHSGrupp = vk.handelssortimentsgruppId !== null && vk.handelssortimentId === null;

                    let konverteringsFaktorObservable: Observable<number>;
                    if(isHSGrupp) {
                        konverteringsFaktorObservable = this.getMattslagKonverteringFaktorHSG(vk.mattslag, vk.handelssortimentsgruppId);
                    } else {
                        konverteringsFaktorObservable = this.getMattslagKonverteringFaktorHS(vk.mattslag, vk.handelssortimentId);
                    }

                    konverteringsFaktorObservable.subscribe((faktor: number) => {
                        konverteringsfaktor = faktor;
                        this.mattslagsKonverteringar.push(
                            {
                                mattslag: vk.mattslag,
                                handelssortimentId: vk.handelssortimentId,
                                handelssortimentsgruppId: vk.handelssortimentsgruppId,
                                konverteringsfaktor: konverteringsfaktor
                            });
                    });
                }
            }
        });
    }

    private mattslagskonverteringExists(vk: Virkeskostnad): boolean {
        const isHSGrupp = vk.handelssortimentsgruppId !== null && vk.handelssortimentId === null;
        if(isHSGrupp) {
            return this.mattslagsKonverteringar.some(x => x.mattslag === vk.mattslag &&
                x.handelssortimentsgruppId === vk.handelssortimentsgruppId &&
                x.handelssortimentId === null);
        } else {
            return this.mattslagsKonverteringar.some(x => x.mattslag === vk.mattslag &&
                x.handelssortimentId === vk.handelssortimentId &&
                x.handelssortimentId !== null);
        }
    }

    private handleEjHanteratUtbyteWarnings() {
        this.ejHanteratUtbyteWarnings.splice(0);
        this.ejHanteratUtbyte.filter(grupp => {
            const kvantitetM3FubKvar = (grupp.kvantitetHa ?? 0) * (this.atgardNettoareal ?? 0);
            return kvantitetM3FubKvar >= 1;
        }).forEach(grupp => {
            const kvantitetM3FubKvar = (grupp.kvantitetHa ?? 0) * (this.atgardNettoareal ?? 0);
            const warningText = `${ grupp.produktgrupp.namn  } kvar att fördela: ${  _.round(kvantitetM3FubKvar, 0)  } ${ Mattslag.M3F }`;
            this.ejHanteratUtbyteWarnings.push({ type: "warning",
                text: warningText } );
        });
    }

    private getMattslagKonverteringFaktorHS(mattslag: string, handelssortimentId?: number): Observable<number> {
        // Kommentar kopierad från gamla VK
        // Produktionsområde är tills vidare hårdkodat till Karlskoga
        // Vi har just nu inget produktionsområde att gå på (Åtgäder kan saknas etc), därav hårdkodning.

        return this.forkalkylService.getAverageMattslag({
            produktionsomradeId: 26,
            mattslag: mattslag,
            handelssortimentId: handelssortimentId
        });
    }

    private getMattslagKonverteringFaktorHSG(mattslag: string, handelssortimentsGruppId: number): Observable<number> {
        // Kommentar kopierad från gamla VK
        // Produktionsområde är tills vidare hårdkodat till Karlskoga
        // Vi har just nu inget produktionsområde att gå på (Åtgärder kan saknas etc), därav hårdkodning.

        return this.forkalkylService.getAverageMattslag({
            produktionsomradeId: 26,
            mattslag: mattslag,
            handelssortimentsGruppId: handelssortimentsGruppId
        });
    }
}
